import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, MoveLeft } from 'lucide-react';

import { tenantApi } from '@/modules/tenant/services/tenantApi';
import { BrandedAuthLayout } from '@/layouts/BrandedAuthLayout';
import { Input } from '@marine/ui/input';
import { Button } from '@marine/ui/button';
import { Alert } from '@marine/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@marine/ui/form';
import { getErrorMessage } from '@marine/utils/getErrorMessage';

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function TenantForgotPasswordPage() {
  const navigate = useNavigate();

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const mutation = useMutation({
    mutationFn: async ({ email }: ForgotPasswordFormData) => {
      return tenantApi.postOne('/auth/forgot-password', { email });
    },
    onSuccess: (_data, vars) => {
      navigate(
        `/reset-password/check-email?email=${encodeURIComponent(vars.email)}`,
      );
    },
  });

  const onSubmit = (data: ForgotPasswordFormData) => {
    mutation.mutate(data);
  };

  return (
    <BrandedAuthLayout variant="dealer">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="block w-full space-y-5"
        >
          <div className="text-center space-y-1 pb-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              Forgot Password
            </h1>
            <p className="text-sm text-muted-foreground">
              Enter your email and we&apos;ll send you a reset link
            </p>
          </div>

          {mutation.isError && (
            <Alert variant="destructive">
              <p className="text-sm">
                {getErrorMessage(
                  mutation.error,
                  'Could not send reset link. Please try again.',
                )}
              </p>
            </Alert>
          )}

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    placeholder="you@dealer.com"
                    type="email"
                    autoComplete="email"
                    disabled={mutation.isPending}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Sending link...
              </span>
            ) : (
              'Send reset link'
            )}
          </Button>

          <div className="text-center text-sm">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm font-semibold text-foreground hover:text-link"
            >
              <MoveLeft className="size-3.5 opacity-70" /> Back to sign in
            </Link>
          </div>
        </form>
      </Form>
    </BrandedAuthLayout>
  );
}
